import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { addReminder, deleteReminder, scheduleTask } from "@/db/operations"
import { parseDbDate } from "@totodo/operations"
import { hapticLight } from "@/shared/utils/platform/haptics"
import { ReminderKind } from "@/db/schema"
import type { DbReminder, DbStartAtIndex } from "@/db/schema"
import { toDbDate, laterTodayTime } from "@/shared/utils/date/dateUtils"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { InfiniteCalendar } from "./InfiniteCalendar"
import { WheelTimePicker } from "./WheelTimePicker"

// ---------------------------------------------------------------------------
// Icons — SVGR imports (pikaicons stroke + totodoicons)
// ---------------------------------------------------------------------------
import BellIcon from "@/shared/icons/pikaicons/stroke/alerts/notification-bell-on.svg?react"
import AlarmIcon from "@/shared/icons/pikaicons/stroke/time/alarm-default.svg?react"
import LightningIcon from "@/shared/icons/pikaicons/stroke/weather/lightning-thunder-electric-on.svg?react"
import CalendarIcon from "@/shared/icons/pikaicons/stroke/time/calendar-event.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import SettingsIcon from "@/shared/icons/totodoicons/stroke/settings.svg?react"
import BeforeIcon from "@/shared/icons/pikaicons/stroke/time/reminder-anticlockwise.svg?react"
import AfterIcon from "@/shared/icons/pikaicons/stroke/time/reminder-clockwise.svg?react"
import ClockIcon from "@/shared/icons/pikaicons/stroke/time/clock-default.svg?react"

interface ReminderSectionProps {
  taskId?: string
  startAtData?: DbStartAtIndex
  existingReminder?: DbReminder
  /** Task deadline (UTC ms string) for "Before deadline" presets */
  dueAt?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * When provided, the picker returns the selected time + kind without saving to DB.
   * Used by RecurrencePicker to calculate offset.
   */
  onSelectTime?: (time: dayjs.Dayjs, kind: ReminderKind) => void
  /** Called when user removes an existing selection (in offset/recurrence mode) */
  onRemove?: () => void
}

type Mode = "quick" | "advanced"

interface Preset {
  key: string
  label: string
  /** Shorter fallback label shown when the button is too narrow */
  shortLabel?: string
  getTime: () => dayjs.Dayjs
  /** True if the time has passed and was bumped to tomorrow */
  isTomorrow?: boolean
}

// ---------------------------------------------------------------------------
// Preset generators
// ---------------------------------------------------------------------------

function getGenericPresets(t: (key: string) => string): Preset[] {
  return [
    {
      key: "later_today",
      label: t("Later today"),
      getTime: () => laterTodayTime(),
    },
    {
      key: "tonight",
      label: t("Tonight"),
      getTime: () => dayjs().hour(21).minute(0).second(0),
    },
    {
      key: "tomorrow_morning",
      label: t("Tomorrow 09:00"),
      shortLabel: t("Tmrw 09:00"),
      getTime: () => dayjs().add(1, "day").hour(9).minute(0).second(0),
    },
    {
      key: "in_10min",
      label: t("In 10 min"),
      getTime: () => dayjs().add(10, "minute"),
    },
    {
      key: "in_30min",
      label: t("In 30 min"),
      getTime: () => dayjs().add(30, "minute"),
    },
    {
      key: "in_1h",
      label: t("In 1 hour"),
      getTime: () => dayjs().add(1, "hour"),
    },
  ]
}

function getTaskDayPresets(taskDate: dayjs.Dayjs): Preset[] {
  const now = dayjs()

  const makePreset = (
    key: string,
    label: string,
    shortLabel: string,
    hour: number,
    minute: number,
  ): Preset => {
    let candidate = taskDate.hour(hour).minute(minute).second(0)
    let displayLabel = label
    let shortDisplayLabel: string | undefined = shortLabel
    const isTomorrow = candidate.isBefore(now)
    if (isTomorrow) {
      candidate = candidate.add(1, "day")
      displayLabel = `Tomorrow ${candidate.format("HH:mm")}`
      shortDisplayLabel = `Tmrw ${candidate.format("HH:mm")}`
    }
    return {
      key,
      label: displayLabel,
      shortLabel: shortDisplayLabel,
      getTime: () => candidate,
      isTomorrow,
    }
  }

  return [
    makePreset("early_morning", "Early morning (07:00)", "Early morning", 7, 0),
    makePreset("morning", "Morning (09:00)", "Morning", 9, 0),
    makePreset("midday", "Midday (12:00)", "Midday", 12, 0),
    makePreset("afternoon", "Afternoon (15:00)", "Afternoon", 15, 0),
    makePreset("evening", "Evening (19:00)", "Evening", 19, 0),
    (() => {
      const candidate = taskDate
        .subtract(1, "day")
        .hour(18)
        .minute(0)
        .second(0)
      const isPast = candidate.isBefore(now)
      const resolved = isPast
        ? now.add(1, "day").hour(18).minute(0).second(0)
        : candidate
      const displayLabel = isPast
        ? `Tomorrow ${resolved.format("HH:mm")}`
        : `Day before (${resolved.format("HH:mm")})`
      const shortDisplayLabel = isPast
        ? `Tmrw ${resolved.format("HH:mm")}`
        : undefined
      // Never dim: resolved time is always in the future
      return {
        key: "day_before",
        label: displayLabel,
        shortLabel: shortDisplayLabel,
        getTime: () => resolved,
        isTomorrow: false,
      }
    })(),
  ]
}

/** Generate "Before deadline" presets when a task has dueAt. */
function getDeadlinePresets(dueAt: string, t: (k: string) => string): Preset[] {
  const deadlineMs = parseDbDate(dueAt)
  if (deadlineMs === undefined) return []
  const deadline = dayjs(deadlineMs)
  const now = dayjs()

  // If deadline is in the past, no presets make sense
  if (deadline.isBefore(now)) return []

  const offsets: { key: string; label: string; shortLabel: string; ms: number }[] = [
    { key: "dl_at", label: t("At deadline"), shortLabel: t("At deadline"), ms: 0 },
    { key: "dl_15m", label: t("15 min before"), shortLabel: t("15 min"), ms: 15 * 60_000 },
    { key: "dl_1h", label: t("1 hour before"), shortLabel: t("1 hour"), ms: 60 * 60_000 },
    { key: "dl_3h", label: t("3 hours before"), shortLabel: t("3 hours"), ms: 3 * 60 * 60_000 },
    { key: "dl_1d", label: t("1 day before"), shortLabel: t("1 day"), ms: 24 * 60 * 60_000 },
    { key: "dl_1w", label: t("1 week before"), shortLabel: t("1 week"), ms: 7 * 24 * 60 * 60_000 },
  ]

  return offsets
    .map(({ key, label, shortLabel, ms }) => {
      const time = deadline.subtract(ms, "millisecond")
      // Only include presets that are still in the future
      if (time.isBefore(now)) return null
      return { key, label, shortLabel, getTime: () => time }
    })
    .filter(Boolean) as Preset[]
}

// ---------------------------------------------------------------------------
// Offset mode presets
// ---------------------------------------------------------------------------

type ReminderMode = "time" | "offset"

const MS_MINUTE = 60_000
const MS_HOUR = 60 * MS_MINUTE

interface OffsetPreset {
  key: string
  label: string
  offsetMs: number
  section: "before" | "after"
}

function getOffsetPresets(
  t: (k: string) => string,
  taskTime: dayjs.Dayjs | null,
): OffsetPreset[] {
  const timeStr = taskTime ? taskTime.format("HH:mm") : ""
  return [
    { key: "off_at", label: taskTime ? `At ${timeStr}` : t("At task time"), offsetMs: 0, section: "before" },
    { key: "off_15m", label: t("15 min before"), offsetMs: -15 * MS_MINUTE, section: "before" },
    { key: "off_30m", label: t("30 min before"), offsetMs: -30 * MS_MINUTE, section: "before" },
    { key: "off_1h", label: t("1 hour before"), offsetMs: -1 * MS_HOUR, section: "before" },
    { key: "off_morning", label: t("Morning of (07:00)"), offsetMs: -(taskTime ? (taskTime.hour() * 60 + taskTime.minute() - 7 * 60) * MS_MINUTE : 2 * MS_HOUR), section: "before" },
    { key: "off_1d", label: t("1 day before"), offsetMs: -24 * MS_HOUR, section: "before" },
    { key: "off_15m_after", label: t("15 min after"), offsetMs: 15 * MS_MINUTE, section: "after" },
    { key: "off_1h_after", label: t("1 hour after"), offsetMs: 1 * MS_HOUR, section: "after" },
    { key: "off_2h_after", label: t("2 hours after"), offsetMs: 2 * MS_HOUR, section: "after" },
    { key: "off_1d_after", label: t("1 day after"), offsetMs: 24 * MS_HOUR, section: "after" },
  ]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Expandable reminder section within the WhenSheet.
 *
 * Two modes:
 * - Quick: Preset chips for common times
 * - Advanced: InfiniteCalendar + WheelTimePicker
 *
 * Matches Things 3 / v1 ReminderPicker UX.
 */
export const ReminderSection = memo(function ReminderSection({
  taskId,
  startAtData,
  existingReminder,
  dueAt,
  open,
  onOpenChange,
  onSelectTime,
  onRemove,
}: ReminderSectionProps) {
  const { t } = useTranslation()
  const { isDesktop } = useBreakpoint()
  const [mode, setMode] = useState<Mode>("quick")

  // ── Reminder mode: absolute time vs offset from task time ──
  const [reminderMode, setReminderMode] = useState<ReminderMode>(() => {
    const stored = localStorage.getItem("totodo:reminder-mode")
    return stored === "offset" ? "offset" : "time"
  })
  const toggleReminderMode = useCallback(() => {
    setReminderMode((m) => {
      const next = m === "time" ? "offset" : "time"
      localStorage.setItem("totodo:reminder-mode", next)
      hapticLight()
      // Offset mode has no "advanced" — reset to quick select
      if (next === "offset") {
        setMode("quick")
      }
      return next
    })
  }, [])

  // ── Task time for offset mode ──
  const taskTime = useMemo(() => {
    const startMs = parseDbDate(startAtData?.startAt)
    if (startMs === undefined) return null
    const d = dayjs(startMs)
    // If time is exactly midnight, task has no explicit time set
    if (d.hour() === 0 && d.minute() === 0) return null
    return d
  }, [startAtData])

  const taskTimeLabel = useMemo(() => {
    if (!taskTime) return null
    const roundedMin = Math.round(taskTime.minute() / 5) * 5
    return taskTime.minute(roundedMin).second(0).format("HH:mm")
  }, [taskTime])

  // Task time editor state (for offset mode when no time is set)
  const [editingTaskTime, setEditingTaskTime] = useState(false)
  const [taskTimeHour, setTaskTimeHour] = useState(() => dayjs().add(1, "hour").hour())
  const [taskTimeMinute, setTaskTimeMinute] = useState(0)

  const offsetPresets = useMemo(
    () => getOffsetPresets(t, taskTime),
    [t, taskTime],
  )
  const beforePresets = useMemo(() => offsetPresets.filter(p => p.section === "before"), [offsetPresets])
  const afterPresets = useMemo(() => offsetPresets.filter(p => p.section === "after"), [offsetPresets])
  // Filter out "Morning of" if offset would be 0 or negative (task before 07:00)
  const filteredBefore = useMemo(() => {
    return beforePresets.filter(p => !(p.key === "off_morning" && p.offsetMs >= 0))
  }, [beforePresets])

  const [selectedOffset, setSelectedOffset] = useState<string | null>("off_15m")
  const [offsetMode, setOffsetMode] = useState<"quick" | "custom">("quick")
  const [customOffsetHours, setCustomOffsetHours] = useState(1)
  const [customOffsetMinutes, setCustomOffsetMinutes] = useState(0)
  const [customOffsetDirection, setCustomOffsetDirection] = useState<"before" | "after">("before")

  // ── Horizontal swipe to toggle mode ──
  const swipeRef = useRef<HTMLDivElement>(null)
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (touch) touchStart.current = { x: touch.clientX, y: touch.clientY }
  }, [])
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return
    const touch = e.changedTouches[0]
    if (!touch) return
    const dx = touch.clientX - touchStart.current.x
    const dy = touch.clientY - touchStart.current.y
    // Only trigger on predominantly horizontal swipe (>60px, angle < 30°)
    if (Math.abs(dx) > 60 && Math.abs(dy) < Math.abs(dx) * 0.5) {
      if (dx < 0) setMode("advanced") // swipe left → advanced
      else setMode("quick") // swipe right → quick
    }
    touchStart.current = null
  }, [])

  const [kind, setKind] = useState<ReminderKind>(() => {
    const stored = localStorage.getItem("totodo:reminder-kind")
    return stored === String(ReminderKind.Alarm) ? ReminderKind.Alarm : ReminderKind.Reminder
  })
  const [saving, setSaving] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<string | null>(
    existingReminder ? null : "in_1h",
  )

  // Advanced mode state
  const [selectedDate, setSelectedDate] = useState(() => dayjs())
  const [hour, setHour] = useState(() => dayjs().add(1, "hour").hour())
  const [minute, setMinute] = useState(0)

  // Task date for task-day presets — normalized to start of day.
  // If the task date is today or in the past, use today as base.
  const taskDate = useMemo(() => {
    const startMs = parseDbDate(startAtData?.startAt)
    if (startMs === undefined) return null
    const d = dayjs(startMs).startOf("day")
    const today = dayjs().startOf("day")
    // Past/today tasks: base presets on today so only past HOURS are dimmed
    return d.isBefore(today) || d.isSame(today, "day") ? today : d
  }, [startAtData])

  const genericPresets = useMemo(() => getGenericPresets(t), [t])
  const taskDayPresets = useMemo(
    () => (taskDate ? getTaskDayPresets(taskDate) : []),
    [taskDate],
  )
  const deadlinePresets = useMemo(
    () => (dueAt ? getDeadlinePresets(dueAt, t) : []),
    [dueAt, t],
  )

  // All presets combined for lookup
  const allPresets = useMemo(
    () => [...genericPresets, ...taskDayPresets, ...deadlinePresets],
    [genericPresets, taskDayPresets, deadlinePresets],
  )

  // The resolved time for the currently selected preset or advanced picker
  const resolvedTime = useMemo(() => {
    // Offset mode: calculate from task time + offset
    if (reminderMode === "offset" && mode === "quick") {
      const baseTime = taskTime || (editingTaskTime
        ? dayjs().hour(taskTimeHour).minute(taskTimeMinute).second(0)
        : null)
      if (!baseTime) return null

      if (offsetMode === "custom") {
        const ms = (customOffsetHours * MS_HOUR + customOffsetMinutes * MS_MINUTE) * (customOffsetDirection === "before" ? -1 : 1)
        if (ms === 0) return baseTime
        return baseTime.add(ms, "millisecond")
      }

      if (selectedOffset) {
        const preset = offsetPresets.find((p) => p.key === selectedOffset)
        if (preset) {
          // For "morning of" when task time < 07:00, just use 07:00
          if (preset.key === "off_morning") {
            const taskStartMs = parseDbDate(startAtData?.startAt)
            const taskDay = taskStartMs !== undefined
              ? dayjs(taskStartMs).startOf("day")
              : dayjs().startOf("day")
            return taskDay.hour(7).minute(0).second(0)
          }
          return baseTime.add(preset.offsetMs, "millisecond")
        }
      }
      return baseTime
    }
    if (mode === "advanced") {
      return selectedDate.hour(hour).minute(minute).second(0)
    }
    if (selectedPreset) {
      const preset = allPresets.find((p) => p.key === selectedPreset)
      if (preset) return preset.getTime()
    }
    const existingFireMs = parseDbDate(existingReminder?.fireAt)
    if (existingFireMs !== undefined) {
      return dayjs(existingFireMs)
    }
    return null
  }, [mode, reminderMode, selectedPreset, selectedOffset, offsetMode, customOffsetHours, customOffsetMinutes, customOffsetDirection, allPresets, offsetPresets, selectedDate, hour, minute, existingReminder, taskTime, editingTaskTime, taskTimeHour, taskTimeMinute, startAtData])

  // Dynamic title: "Remind me today 20:40" / offset info
  const sheetTitle = useMemo(() => {
    if (reminderMode === "offset" && mode === "quick") {
      const prefix = kind === ReminderKind.Alarm ? t("Alarm") : t("Remind me")
      if (offsetMode === "custom") {
        const h = customOffsetHours
        const m = customOffsetMinutes
        const dir = customOffsetDirection
        const parts: string[] = []
        if (h > 0) parts.push(`${h}h`)
        if (m > 0) parts.push(`${m}min`)
        const offsetStr = parts.length > 0 ? `${parts.join(" ")} ${dir}` : t("at task time")
        return `${prefix} ${offsetStr}`
      }
      if (selectedOffset) {
        const preset = offsetPresets.find((p) => p.key === selectedOffset)
        if (preset) {
          return `${prefix} ${preset.label.toLowerCase()}`
        }
      }
    }
    if (resolvedTime) {
      const now = dayjs()
      const dateStr = resolvedTime.isSame(now, "day")
        ? t("today")
        : resolvedTime.isSame(now.add(1, "day"), "day")
          ? t("tomorrow")
          : resolvedTime.format("ddd D MMM")
      const timeStr = resolvedTime.format("HH:mm")
      return kind === ReminderKind.Alarm
        ? `${t("Alarm")} ${dateStr} ${timeStr}`
        : `${t("Remind me")} ${dateStr} ${timeStr}`
    }
    const existingFireMs = parseDbDate(existingReminder?.fireAt)
    if (existingFireMs !== undefined) {
      const d = dayjs(existingFireMs)
      const dateStr = d.isSame(dayjs(), "day")
        ? t("today")
        : d.format("ddd D MMM")
      return `${t("Remind me")} ${dateStr} ${d.format("HH:mm")}`
    }
    return t("Add Reminder")
  }, [resolvedTime, existingReminder, kind, t, reminderMode, mode, selectedOffset, offsetMode, customOffsetHours, customOffsetMinutes, customOffsetDirection, offsetPresets])

  // Save button label: preset label or formatted time
  const actionButtonLabel = useMemo(() => {
    if (reminderMode === "offset" && mode === "quick") {
      if (offsetMode === "custom" && resolvedTime) {
        const h = customOffsetHours
        const m = customOffsetMinutes
        const parts: string[] = []
        if (h > 0) parts.push(`${h}h`)
        if (m > 0) parts.push(`${m}min`)
        const offsetStr = parts.length > 0 ? `${parts.join(" ")} ${customOffsetDirection}` : t("At task time")
        return `${offsetStr} → ${resolvedTime.format("HH:mm")}`
      }
      if (selectedOffset) {
        const preset = offsetPresets.find((p) => p.key === selectedOffset)
        if (preset && resolvedTime) {
          return `${preset.label} → ${resolvedTime.format("HH:mm")}`
        }
        if (preset) return preset.label
      }
    }
    if (mode === "quick" && selectedPreset) {
      const preset = allPresets.find((p) => p.key === selectedPreset)
      if (preset) return preset.label
    }
    if (resolvedTime) {
      const now = dayjs()
      const dateStr = resolvedTime.isSame(now, "day")
        ? t("Today")
        : resolvedTime.isSame(now.add(1, "day"), "day")
          ? t("Tomorrow")
          : resolvedTime.format("ddd D MMM")
      return `${dateStr} ${resolvedTime.format("HH:mm")}`
    }
    return t("Save")
  }, [mode, reminderMode, offsetMode, customOffsetHours, customOffsetMinutes, customOffsetDirection, selectedPreset, selectedOffset, allPresets, offsetPresets, resolvedTime, t])

  // Initialize from existing reminder
  useEffect(() => {
    const existingFireMs = parseDbDate(existingReminder?.fireAt)
    if (existingFireMs !== undefined) {
      const d = dayjs(existingFireMs)
      setSelectedDate(d)
      setHour(d.hour())
      setMinute(d.minute())
      setKind((existingReminder?.kind as ReminderKind) || ReminderKind.Reminder)
    }
  }, [existingReminder])

  // ── Handlers ────────────────────────────────────────────────────

  const handlePresetSelect = useCallback((preset: Preset) => {
    setSelectedPreset(preset.key)
  }, [])

  const handleOffsetSelect = useCallback((preset: OffsetPreset) => {
    setSelectedOffset(preset.key)
  }, [])

  const handleTaskTimeChange = useCallback((h24: number, m: number) => {
    setTaskTimeHour(h24)
    setTaskTimeMinute(m)
  }, [])

  const handleSetTaskTime = useCallback(async () => {
    const currentStartMs = parseDbDate(startAtData?.startAt)
    if (!taskId || !startAtData || currentStartMs === undefined) return
    // Update startAt to include the new time (keeping same date)
    const currentDate = dayjs(currentStartMs).startOf("day")
    const newStartAt = currentDate.hour(taskTimeHour).minute(taskTimeMinute).second(0)
    await scheduleTask(
      taskId,
      toDbDate(newStartAt),
      startAtData.type,
      startAtData.subType,
    )
    setEditingTaskTime(false)
  }, [taskId, startAtData, taskTimeHour, taskTimeMinute])

  const handleSave = useCallback(async () => {
    if (!resolvedTime) return
    if (onSelectTime) {
      onSelectTime(resolvedTime, kind)
      onOpenChange(false)
      return
    }
    if (!taskId) return
    setSaving(true)
    try {
      if (existingReminder?.id) {
        await deleteReminder(existingReminder.id)
      }
      await addReminder(taskId, toDbDate(resolvedTime), kind)
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }, [taskId, resolvedTime, kind, existingReminder, onOpenChange, onSelectTime])

  const handleAdvancedSave = useCallback(async () => {
    let reminderDateTime = selectedDate
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0)

    // Auto-advance if in the past
    if (reminderDateTime.isBefore(dayjs())) {
      reminderDateTime = reminderDateTime.add(1, "day")
      setSelectedDate(reminderDateTime)
    }

    if (onSelectTime) {
      onSelectTime(reminderDateTime, kind)
      onOpenChange(false)
      return
    }
    if (!taskId) return

    setSaving(true)
    try {
      if (existingReminder?.id) {
        await deleteReminder(existingReminder.id)
      }

      await addReminder(taskId, toDbDate(reminderDateTime), kind)
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }, [taskId, selectedDate, hour, minute, kind, existingReminder, onOpenChange, onSelectTime])

  const handleDelete = useCallback(async () => {
    if (onRemove) {
      onRemove()
      onOpenChange(false)
      return
    }
    if (!existingReminder?.id) return
    await deleteReminder(existingReminder.id)
    onOpenChange(false)
  }, [existingReminder, onOpenChange, onRemove])

  const handleCalendarSelect = useCallback((date: dayjs.Dayjs) => {
    setSelectedDate(date)
  }, [])

  const handleTimeChange = useCallback((h24: number, m: number) => {
    setHour(h24)
    setMinute(m)
  }, [])

  const toggleKind = useCallback(() => {
    setKind((k) => {
      const next = k === ReminderKind.Reminder ? ReminderKind.Alarm : ReminderKind.Reminder
      localStorage.setItem("totodo:reminder-kind", String(next))
      hapticLight()
      return next
    })
  }, [])

  // ── Render ──────────────────────────────────────────────────────

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={onOpenChange}
      title={sheetTitle}
    >
      <div className="flex flex-col overflow-y-auto scroll-smooth scrollbar-none pb-4">
        <div
          ref={swipeRef}
          className="flex flex-col px-4 pt-1"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* ── Title + mode toggle ── */}
          <div className="relative flex items-center justify-center pb-5 pt-0">
            <h2 className="text-center text-xl font-bold">
              {sheetTitle}
            </h2>
            {/* Compact mode toggle — top right */}
            <button
              type="button"
              onClick={toggleReminderMode}
              title={reminderMode === "time" ? t("Switch to offset mode") : t("Switch to time mode")}
              className={cn(
                "absolute right-0 flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-medium transition-all",
                reminderMode === "offset"
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground hover:text-foreground",
              )}
            >
              {reminderMode === "offset" ? (
                <BeforeIcon className="size-3.5" />
              ) : (
                <ClockIcon className="size-3.5" />
              )}
              <span className="hidden sm:inline">{reminderMode === "offset" ? t("Before") : t("Time")}</span>
            </button>
          </div>

          {/* ── Offset mode ── */}
          {reminderMode === "offset" && mode === "quick" && (
            <div className="flex flex-col gap-4">
              {/* Task time reference / subtitle */}
              {taskTime ? (
                <p className="-mt-1 mb-1 text-center text-sm text-muted-foreground">
                  {t("Relative to task time")}
                  {" "}
                  <span className="font-semibold text-foreground">{taskTimeLabel}</span>
                </p>
              ) : !editingTaskTime ? (
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setEditingTaskTime(true)}
                    className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-400 dark:hover:bg-amber-950/50"
                  >
                    <ClockIcon className="size-3.5" />
                    {t("Set task time")}
                  </button>
                </div>
              ) : null}

              {/* Inline task time editor */}
              {editingTaskTime && !taskTime && (
                <div className="flex flex-col items-center gap-2 rounded-xl bg-muted/50 p-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {t("Task time")}
                  </span>
                  {isDesktop ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={String(taskTimeHour).padStart(2, "0")}
                        onChange={(e) => {
                          const v = Math.min(23, Math.max(0, Number(e.target.value)))
                          handleTaskTimeChange(v, taskTimeMinute)
                        }}
                        className="w-14 rounded-md border border-border bg-background px-2 py-2 text-center text-lg font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <span className="text-lg font-bold text-muted-foreground">:</span>
                      <input
                        type="number"
                        min={0}
                        max={59}
                        value={String(taskTimeMinute).padStart(2, "0")}
                        onChange={(e) => {
                          const v = Math.min(59, Math.max(0, Number(e.target.value)))
                          handleTaskTimeChange(taskTimeHour, v)
                        }}
                        className="w-14 rounded-md border border-border bg-background px-2 py-2 text-center text-lg font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                  ) : (
                    <WheelTimePicker
                      hour={taskTimeHour}
                      minute={taskTimeMinute}
                      onTimeChange={handleTaskTimeChange}
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleSetTaskTime}
                    className="rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    {t("Set time")}
                  </button>
                </div>
              )}

              {/* Quick: Before / After preset grids */}
              {offsetMode === "quick" && (
                <div className="flex flex-col gap-4">
                  {/* Before presets */}
                  <div>
                    <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                      <BeforeIcon className="size-3.5" /> {t("Before")}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {filteredBefore.map((p) => {
                        const isDisabled = !taskTime && !editingTaskTime && p.key !== "off_at"
                        return (
                          <button
                            key={p.key}
                            type="button"
                            disabled={saving || isDisabled}
                            onClick={() => handleOffsetSelect(p)}
                            className={cn(
                              "rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                              "transition-all active:scale-[0.97]",
                              selectedOffset === p.key
                                ? "bg-primary/10 text-primary"
                                : "bg-muted text-foreground hover:bg-muted/80",
                              isDisabled && "opacity-40",
                            )}
                          >
                            {p.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* After presets */}
                  <div>
                    <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                      <AfterIcon className="size-3.5" /> {t("After")}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {afterPresets.map((p) => {
                        const isDisabled = !taskTime && !editingTaskTime
                        return (
                          <button
                            key={p.key}
                            type="button"
                            disabled={saving || isDisabled}
                            onClick={() => handleOffsetSelect(p)}
                            className={cn(
                              "rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                              "transition-all active:scale-[0.97]",
                              selectedOffset === p.key
                                ? "bg-primary/10 text-primary"
                                : "bg-muted text-foreground hover:bg-muted/80",
                              isDisabled && "opacity-40",
                            )}
                          >
                            {p.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Custom: h + min + before/after — single row */}
              {offsetMode === "custom" && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <input
                    type="number"
                    min={0}
                    max={72}
                    value={customOffsetHours}
                    onChange={(e) => setCustomOffsetHours(Math.max(0, Number(e.target.value)))}
                    className="w-14 rounded-md border border-border px-2 py-2.5 text-center text-sm"
                  />
                  <span className="text-sm text-muted-foreground">h</span>
                  <input
                    type="number"
                    min={0}
                    max={59}
                    step={5}
                    value={customOffsetMinutes}
                    onChange={(e) => setCustomOffsetMinutes(Math.max(0, Math.min(59, Number(e.target.value))))}
                    className="w-14 rounded-md border border-border px-2 py-2.5 text-center text-sm"
                  />
                  <span className="text-sm text-muted-foreground">min</span>

                  {/* Before / After toggle */}
                  <div className="inline-flex overflow-hidden rounded-lg bg-muted p-0.5">
                    <button
                      type="button"
                      onClick={() => setCustomOffsetDirection("before")}
                      className={cn(
                        "rounded-md px-3 py-2 text-xs font-medium transition-all",
                        customOffsetDirection === "before"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {t("Before")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCustomOffsetDirection("after")}
                      className={cn(
                        "rounded-md px-3 py-2 text-xs font-medium transition-all",
                        customOffsetDirection === "after"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {t("After")}
                    </button>
                  </div>
                </div>
              )}

              {/* Segmented control: Quick / Custom (within offset mode) */}
              <div className="flex items-center justify-center pt-2 pb-1">
                <div className="inline-flex rounded-xl bg-muted p-1">
                  <button
                    type="button"
                    onClick={() => setOffsetMode("quick")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all",
                      offsetMode === "quick"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <LightningIcon className="size-3.5" /> {t("Quick")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOffsetMode("custom")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all",
                      offsetMode === "custom"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <SettingsIcon className="size-3.5" /> {t("Custom")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Quick mode (absolute time) ── */}
          {reminderMode === "time" && mode === "quick" && (
            <div className="flex flex-col gap-4">
              {/* Generic presets */}
              <div>
                <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  <LightningIcon className="size-3.5" /> {t("Quick select")}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {genericPresets.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      disabled={saving}
                      onClick={() => handlePresetSelect(p)}
                      className={cn(
                        "@container min-w-0 rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                        "transition-all active:scale-[0.97]",
                        selectedPreset === p.key
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-foreground hover:bg-muted/80",
                      )}
                    >
                      {p.shortLabel ? (
                        <>
                          <span className="@min-[8rem]:hidden">{p.shortLabel}</span>
                          <span className="hidden @min-[8rem]:inline">{p.label}</span>
                        </>
                      ) : (
                        <span className="truncate">{p.label}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Task-day presets */}
              {taskDayPresets.length > 0 && taskDate && (
                <div>
                  <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    <CalendarIcon className="size-3.5" /> {t("Based on task day")} ({taskDate.format("ddd D MMM").toUpperCase()})
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {taskDayPresets.map((p) => {
                      return (
                        <button
                          key={p.key}
                          type="button"
                          disabled={saving}
                          onClick={() => handlePresetSelect(p)}
                          className={cn(
                            "@container min-w-0 rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                            "transition-all active:scale-[0.97]",
                            selectedPreset === p.key
                              ? "bg-primary/10 text-primary"
                              : "bg-muted text-foreground hover:bg-muted/80",
                          )}
                        >
                          {p.shortLabel ? (
                            <>
                              <span className="@min-[8rem]:hidden">{p.shortLabel}</span>
                              <span className="hidden @min-[8rem]:inline">{p.label}</span>
                            </>
                          ) : (
                            <span className="truncate">{p.label}</span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Deadline presets */}
              {deadlinePresets.length > 0 && dueAt && (
                <div>
                  <div className="flex items-center gap-1.5 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    <CalendarIcon className="size-3.5" /> {t("Before deadline")} ({dayjs(parseDbDate(dueAt)).format("ddd D MMM HH:mm").toUpperCase()})
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {deadlinePresets.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        disabled={saving}
                        onClick={() => handlePresetSelect(p)}
                        className={cn(
                          "@container min-w-0 rounded-2xl px-3 py-2.5 text-[13px] font-medium",
                          "transition-all active:scale-[0.97]",
                          selectedPreset === p.key
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-foreground hover:bg-muted/80",
                        )}
                      >
                        {p.shortLabel ? (
                          <>
                            <span className="@min-[8rem]:hidden">{p.shortLabel}</span>
                            <span className="hidden @min-[8rem]:inline">{p.label}</span>
                          </>
                        ) : (
                          <span className="truncate">{p.label}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Advanced mode ── */}
          {mode === "advanced" && (
            <div className="flex flex-col gap-3">
              {/* Calendar */}
              <InfiniteCalendar
                selectedDate={selectedDate}
                onSelect={handleCalendarSelect}
                minDate={dayjs()}
                height={220}
              />

              {/* Time picker — wheel on mobile, inputs on desktop */}
              <div className="flex flex-col items-center gap-1">
                {isDesktop ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={String(hour).padStart(2, "0")}
                      onChange={(e) => {
                        const v = Math.min(23, Math.max(0, Number(e.target.value)))
                        handleTimeChange(v, minute)
                      }}
                      className="w-14 rounded-md border border-border bg-muted px-2 py-2 text-center text-lg font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <span className="text-lg font-bold text-muted-foreground">:</span>
                    <input
                      type="number"
                      min={0}
                      max={59}
                      value={String(minute).padStart(2, "0")}
                      onChange={(e) => {
                        const v = Math.min(59, Math.max(0, Number(e.target.value)))
                        handleTimeChange(hour, v)
                      }}
                      className="w-14 rounded-md border border-border bg-muted px-2 py-2 text-center text-lg font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                ) : (
                  <WheelTimePicker
                    hour={hour}
                    minute={minute}
                    onTimeChange={handleTimeChange}
                    fullWidth
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Segmented control: Quick / Advanced (only in time mode) ── */}
          {reminderMode === "time" && (
          <div className="flex items-center justify-center pt-4 pb-1">
            <div className="inline-flex rounded-xl bg-muted p-1">
              <button
                type="button"
                onClick={() => setMode("quick")}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all",
                  mode === "quick"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <LightningIcon className="size-3.5" /> {t("Quick select")}
              </button>
              <button
                type="button"
                onClick={() => setMode("advanced")}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all",
                  mode === "advanced"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <SettingsIcon className="size-3.5" /> {t("Advanced")}
              </button>
            </div>
          </div>
          )}

          {/* Delete existing reminder */}
          {(existingReminder?.id || onRemove) && (
            <button
              type="button"
              onClick={handleDelete}
              className={cn(
                "mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-medium",
                "text-destructive transition-colors hover:bg-destructive/10",
              )}
            >
              <CrossIcon className="size-4" />
              {t("Remove reminder")}
            </button>
          )}
        </div>

        {/* ── Footer bar: Cancel | [Kind toggle + Save] grouped ── */}
        <div className="mt-4 flex items-center gap-3 border-t border-border/30 px-4 pt-3 pb-2">
          {/* Cancel */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t("Cancel")}
          </button>

          {/* Grouped button: [Kind toggle | Save] */}
          <div className="flex flex-1 overflow-hidden rounded-lg">
            {/* Kind toggle (left segment) */}
            <button
              type="button"
              onClick={toggleKind}
              title={kind === ReminderKind.Reminder ? t("Switch to alarm") : t("Switch to reminder")}
              className={cn(
                "flex items-center justify-center px-3 py-2.5 transition-colors",
                "bg-primary text-primary-foreground hover:bg-primary/90",
              )}
            >
              {kind === ReminderKind.Reminder ? (
                <BellIcon className="size-5" />
              ) : (
                <AlarmIcon className="size-5" />
              )}
            </button>

            {/* Save button (right segment) */}
            <button
              type="button"
              disabled={saving || (
                mode === "quick" && !existingReminder && (
                  reminderMode === "time" ? !selectedPreset : (!selectedOffset || (!taskTime && !editingTaskTime))
                )
              )}
              onClick={mode === "advanced" ? handleAdvancedSave : handleSave}
              className={cn(
                "flex-1 border-l border-primary-foreground/20 bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground",
                "transition-colors hover:bg-primary/90",
                "disabled:opacity-40",
              )}
            >
              {saving ? t("Saving...") : actionButtonLabel}
            </button>
          </div>
        </div>

        {/* Safe area spacer */}
        <div className="safe-area-bottom" />
      </div>
    </ResponsiveSheet>
  )
})
